import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';
import { fmtDate } from '../dateUtils';
import DateInput from '../DateInput';

const UOMS = ['Lsum', 'PCs', 'TONs', 'Acrs', 'Kgs', 'Ltrs', 'BAGs', 'PAKs', 'CANs', 'Nos'];

const EMPTY = {
  cropId: '', entryDate: new Date().toISOString().split('T')[0],
  itemType: '', vendorName: '', billNo: '', description: '',
  uom: 'Lsum', nou: '1', rpu: '', discount: '0',
  amount: '', totalAmount: '', paymentMode: '', notes: ''
};

function fmt(v) { return v != null && v !== '' ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'; }

export default function AquaExpenses({ cropId, cropCode, seedCost = 0, activeCrops = [], role = 'admin' }) {
  const canWrite = role !== 'readonly';
  const canDelete = role === 'admin';
  const [rows, setRows]               = useState([]);
  const [summary, setSummary]         = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [form, setForm]               = useState({ ...EMPTY, cropId: cropId || '' });
  const [editId, setEditId]           = useState(null);
  const [tab, setTab]                 = useState('list');
  const [filterCrop, setFilterCrop]   = useState(cropId || '');
  const [loading, setLoading]         = useState(true);
  const [err, setErr]                 = useState('');

  // Expense-type management state
  const [typeForm, setTypeForm]       = useState({ name: '' });
  const [typeEditId, setTypeEditId]   = useState(null);
  const [typeErr, setTypeErr]         = useState('');
  const [typeSaving, setTypeSaving]   = useState(false);

  // View-tab extra data
  const [viewHarvest, setViewHarvest] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const loadTypes = () =>
    aquaApi.getExpenseTypes()
      .then(data => {
        if (Array.isArray(data)) {
          const sorted = data.slice().sort((a, b) => a.name.localeCompare(b.name));
          setExpenseTypes(sorted);
          setForm(f => ({ ...f, itemType: f.itemType || (sorted[0]?.name || '') }));
        }
      })
      .catch(() => {});

  const load = () => {
    setLoading(true);
    Promise.all([
      aquaApi.getExpenses(filterCrop || null),
      filterCrop ? aquaApi.getExpenseSummary(filterCrop) : Promise.resolve([])
    ]).then(([r, s]) => { setRows(r); setSummary(s); }).finally(() => setLoading(false));
  };

  useEffect(() => { loadTypes(); }, []);
  useEffect(() => { load(); }, [filterCrop]);
  useEffect(() => { setFilterCrop(cropId || ''); }, [cropId]);
  useEffect(() => { setForm(f => ({ ...f, cropId: cropId || '' })); }, [cropId]);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'nou' || k === 'rpu') {
      const nou = parseFloat(k === 'nou' ? v : next.nou) || 0;
      const rpu = parseFloat(k === 'rpu' ? v : next.rpu) || 0;
      next.amount = (nou * rpu).toFixed(2);
      next.totalAmount = next.amount;
    }
    return next;
  });

  const isExpenseFormValid = !!(
    form.cropId &&
    form.vendorName.trim() &&
    form.billNo.trim() &&
    form.description.trim() &&
    parseFloat(form.amount) > 0 &&
    form.paymentMode.trim()
  );

  const handleSave = async () => {
    if (!isExpenseFormValid) {
      const missing = [];
      if (!form.cropId) missing.push('Crop');
      if (!form.vendorName.trim()) missing.push('Vendor Name');
      if (!form.billNo.trim()) missing.push('Bill No');
      if (!form.description.trim()) missing.push('Description');
      if (!(parseFloat(form.amount) > 0)) missing.push('Amount');
      if (!form.paymentMode.trim()) missing.push('Payment Mode');
      setErr('Please fill required fields: ' + missing.join(', '));
      return;
    }
    try {
      if (editId) await aquaApi.updateExpense(editId, form);
      else await aquaApi.createExpense(form);
      setForm({ ...EMPTY, cropId: cropId || '', itemType: expenseTypes[0]?.name || '' });
      setEditId(null); setErr(''); setTab('list'); load();
    } catch (e) { setErr(e.message); }
  };

  const handleEdit = (r) => {
    setForm({
      cropId: r.crop_id, entryDate: r.entry_date || '',
      itemType: r.item_type || '', vendorName: r.vendor_name || '',
      billNo: r.bill_no || '', description: r.description || '',
      uom: r.uom || 'Lsum', nou: r.nou || '1', rpu: r.rpu || '',
      discount: r.discount || '0', amount: r.amount || '',
      totalAmount: r.total_amount || '', paymentMode: r.payment_mode || '', notes: r.notes || ''
    });
    setEditId(r.id); setTab('form');
  };

  // Expense type CRUD handlers
  const handleTypeSave = async () => {
    const name = typeForm.name.trim();
    if (!name) { setTypeErr('Name is required'); return; }
    setTypeSaving(true); setTypeErr('');
    try {
      const res = typeEditId
        ? await aquaApi.updateExpenseType(typeEditId, { name })
        : await aquaApi.createExpenseType({ name });
      if (res?.error) { setTypeErr(res.error); return; }
      setTypeForm({ name: '' }); setTypeEditId(null);
      await loadTypes();
    } catch (e) { setTypeErr(e.message); }
    finally { setTypeSaving(false); }
  };

  const handleTypeEdit = (t) => { setTypeForm({ name: t.name }); setTypeEditId(t.id); setTypeErr(''); };
  const handleTypeCancel = () => { setTypeForm({ name: '' }); setTypeEditId(null); setTypeErr(''); };

  const handleTypeDelete = async (id) => {
    if (!window.confirm('Delete this expense type?')) return;
    await aquaApi.deleteExpenseType(id);
    await loadTypes();
  };

  useEffect(() => {
    if (tab !== 'view' || !filterCrop) return;
    setViewLoading(true);
    aquaApi.getHarvestSummary(filterCrop)
      .then(d => setViewHarvest(d))
      .catch(() => setViewHarvest(null))
      .finally(() => setViewLoading(false));
  }, [tab, filterCrop]);

  const grandTotal    = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const seedCostVal   = parseFloat(filterCrop ? seedCost : 0) || 0;
  const totalWithSeed = grandTotal + seedCostVal;
  const showCropCol   = !filterCrop;

  if (loading) return <div className="loading-msg">Loading expenses…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list'   ? ' active' : ''}`} onClick={() => { setTab('list'); setEditId(null); }}>📋 Ledger</button>
        {canWrite && <button className={`tab${tab === 'form' ? ' active' : ''}`} onClick={() => setTab('form')}>{editId ? '✏️ Edit' : '➕ Add Expense'}</button>}
        <button className={`tab${tab === 'report' ? ' active' : ''}`} onClick={() => setTab('report')}>📊 Summary</button>
        <button className={`tab${tab === 'view'   ? ' active' : ''}`} onClick={() => setTab('view')}>📑 View Expenses</button>
        {canWrite && <button className={`tab${tab === 'types' ? ' active' : ''}`} onClick={() => setTab('types')}>⚙️ Expense Types</button>}
      </div>

      {/* Crop filter banner */}
      <div className="aq-filter-bar" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 12px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #e8eaed' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#5f6368' }}>🦐 Crop:</span>
        <select className="form-control" style={{ width: 200, padding: '4px 8px' }}
          value={filterCrop} onChange={e => { setFilterCrop(e.target.value); setForm(f => ({ ...f, cropId: e.target.value })); }}>
          <option value="">All Active Crops</option>
          {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
        </select>
        {filterCrop && <span style={{ fontSize: 12, color: '#888' }}>Showing data for selected crop only</span>}
        {!filterCrop && <span style={{ fontSize: 12, color: '#1a73e8', fontWeight: 600 }}>Showing all crops</span>}
      </div>

      {/* ── Ledger tab ── */}
      {tab === 'list' && (() => {
        // Compute per-type cumulative in ledger display order (chronological)
        const typeCum = {};
        const ledgerRows = rows.map(r => {
          const amt = parseFloat(r.amount || 0);
          const t = r.item_type || '';
          typeCum[t] = (typeCum[t] || 0) + amt;
          return { ...r, _typeCum: typeCum[t] };
        });
        const colSpanBase = showCropCol ? 12 : 11;

        return (
        <div>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-icon red" style={{ fontSize: 22 }}>💸</div>
              <div><div className="stat-label">Total Expenses</div><div className="stat-value">₹{fmt(grandTotal)}</div></div>
            </div>
            {seedCostVal > 0 && (
              <div className="stat-card">
                <div className="stat-icon green"><i className="fas fa-seedling"></i></div>
                <div><div className="stat-label">Seed Cost</div><div className="stat-value">₹{fmt(seedCostVal)}</div></div>
              </div>
            )}
            {seedCostVal > 0 && (
              <div className="stat-card">
                <div className="stat-icon orange"><i className="fas fa-calculator"></i></div>
                <div><div className="stat-label">Total incl. Seed</div><div className="stat-value">₹{fmt(totalWithSeed)}</div></div>
              </div>
            )}
            <div className="stat-card">
              <div className="stat-icon blue" style={{ fontSize: 22 }}>🧾</div>
              <div><div className="stat-label">Entries</div><div className="stat-value">{rows.length}</div></div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">💰 Expense Ledger {filterCrop && cropCode ? `— ${cropCode}` : ''}</div>
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    {showCropCol && <th>Crop</th>}
                    <th>Date</th><th>Expense Type</th><th>Vendor</th><th>Bill No</th>
                    <th>Description</th><th>UOM</th><th style={{ textAlign: 'right' }}>NOU</th>
                    <th style={{ textAlign: 'right' }}>RPU</th><th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'right' }}>Type Cum.</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerRows.length === 0 && <tr><td colSpan={colSpanBase} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No expenses recorded</td></tr>}
                  {ledgerRows.map(r => (
                    <tr key={r.id}>
                      {showCropCol && (
                        <td><span style={{ background: '#e8f0fe', color: '#1a73e8', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{r.crop_code}</span></td>
                      )}
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.entry_date)}</td>
                      <td><span style={{ background: '#e8f0fe', color: '#1a73e8', borderRadius: 8, padding: '2px 8px', fontSize: 12 }}>{r.item_type}</span></td>
                      <td>{r.vendor_name}</td>
                      <td>{r.bill_no}</td>
                      <td style={{ maxWidth: 180 }}>{r.description}</td>
                      <td>{r.uom}</td>
                      <td style={{ textAlign: 'right' }}>{r.nou}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(r.rpu)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>₹{fmt(r.amount)}</td>
                      <td style={{ textAlign: 'right', color: '#1a73e8', fontSize: 13 }}>₹{fmt(r._typeCum)}</td>
                      <td>
                        {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleEdit(r)} style={{ marginRight: 4 }}>Edit</button>}
                        {canDelete && <button className="btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete?')) { await aquaApi.deleteExpense(r.id); load(); } }}>Del</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {ledgerRows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={showCropCol ? 9 : 8} style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>TOTAL</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#c5221f' }}>₹{fmt(grandTotal)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Add / Edit Expense tab ── */}
      {tab === 'form' && (
        <div className="card">
          <div className="card-header"><div className="card-title">{editId ? '✏️ Edit Expense' : '➕ Add Expense'}</div></div>
          <div className="card-body">
            {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Crop <span style={{ color: '#c5221f' }}>*</span></label>
                <select className="form-control" value={form.cropId} onChange={e => set('cropId', e.target.value)}
                  style={!form.cropId ? {} : {}}>
                  <option value="">— Select Crop —</option>
                  {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
                </select>
                {!form.cropId && <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Required</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <DateInput className="form-control" value={form.entryDate} onChange={e => set('entryDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Expense Type</label>
                <select className="form-control" value={form.itemType} onChange={e => set('itemType', e.target.value)}>
                  <option value="">— Select —</option>
                  {expenseTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Vendor Name <span style={{ color: '#c5221f' }}>*</span></label>
                <input className="form-control" placeholder="Supplier / Party name" value={form.vendorName}
                  onChange={e => set('vendorName', e.target.value)}
                  style={!form.vendorName.trim() ? {} : {}} />
                {!form.vendorName.trim() && <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Required</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Bill No <span style={{ color: '#c5221f' }}>*</span></label>
                <input className="form-control" placeholder="Bill / Invoice no" value={form.billNo}
                  onChange={e => set('billNo', e.target.value)}
                  style={!form.billNo.trim() ? {} : {}} />
                {!form.billNo.trim() && <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Required</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Payment Mode <span style={{ color: '#c5221f' }}>*</span></label>
                <input className="form-control" placeholder="Cash / UPI / Bank / NEFT" value={form.paymentMode} onChange={e => set('paymentMode', e.target.value)}
                  style={!form.paymentMode.trim() ? {} : {}} />
                {!form.paymentMode.trim() && <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Required</div>}
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Description <span style={{ color: '#c5221f' }}>*</span></label>
                <input className="form-control" placeholder="What was purchased / paid for" value={form.description} onChange={e => set('description', e.target.value)}
                  style={!form.description.trim() ? {} : {}} />
                {!form.description.trim() && <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Required</div>}
              </div>
              <div className="form-group">
                <label className="form-label">UOM</label>
                <select className="form-control" value={form.uom} onChange={e => set('uom', e.target.value)}>
                  {UOMS.map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">NOU (No. of Units)</label>
                <input className="form-control" type="number" step="0.0001" value={form.nou} onChange={e => set('nou', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">RPU (Rate per Unit ₹)</label>
                <input className="form-control" type="number" step="0.01" value={form.rpu}
                  onChange={e => set('rpu', e.target.value)}
                  onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) set('rpu', v.toFixed(2)); }} />
              </div>
              <div className="form-group">
                <label className="form-label">Amount ₹ <span style={{ color: '#c5221f' }}>*</span></label>
                <input className="form-control" type="number" step="0.01" value={form.amount}
                  onChange={e => set('amount', e.target.value)}
                  onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) set('amount', v.toFixed(2)); }}
                  style={!(parseFloat(form.amount) > 0) ? {} : {}} />
                {!(parseFloat(form.amount) > 0) && <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Required — enter a value &gt; 0</div>}
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={!isExpenseFormValid}
                style={{ opacity: isExpenseFormValid ? 1 : 0.45, cursor: isExpenseFormValid ? 'pointer' : 'not-allowed' }}>
                {editId ? 'Update' : 'Save'}
              </button>
              <button className="btn btn-secondary" onClick={() => { setTab('list'); setEditId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Summary tab ── */}
      {tab === 'report' && (() => {
        // Sort by expense type then date
        const sorted = [...rows].sort((a, b) => {
          const tc = (a.item_type || '').localeCompare(b.item_type || '');
          return tc !== 0 ? tc : (a.entry_date || '').localeCompare(b.entry_date || '');
        });

        // Build enriched rows with per-type cumulative and overall running total
        const typeTotals = {};
        let running = 0;
        const enriched = sorted.map(r => {
          const amt = parseFloat(r.amount || 0);
          const t = r.item_type || '—';
          typeTotals[t] = (typeTotals[t] || 0) + amt;
          running += amt;
          return { ...r, _typeCum: typeTotals[t], _runCum: running };
        });

        // Group into blocks by type (preserving sort order)
        const blocks = [];
        let cur = null;
        enriched.forEach(r => {
          const t = r.item_type || '—';
          if (!cur || cur.type !== t) { cur = { type: t, rows: [] }; blocks.push(cur); }
          cur.rows.push(r);
        });

        const grandAmt  = running;
        const grandTotal = grandAmt + seedCostVal;

        const thR = { textAlign: 'right' };
        const tdR = { textAlign: 'right' };
        const SUB = { background: 'rgba(26,115,232,.08)', fontWeight: 700 };
        const GRAND = { fontWeight: 800, borderTop: '2px solid #1a73e8' };

        return (
          <div>
            <div className="card">
              <div className="card-header">
                <div className="card-title">📊 Expense Summary {filterCrop && cropCode ? `— ${cropCode}` : ''}</div>
              </div>
              <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
                {rows.length === 0
                  ? <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>No expense data</div>
                  : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Expense Type</th>
                        <th>Description</th>
                        <th style={thR}>Amount (₹)</th>
                        <th style={thR}>Type Cumulative (₹)</th>
                        <th style={thR}>Running Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blocks.map(block => [
                        // Transaction rows for this type
                        ...block.rows.map((r, i) => (
                          <tr key={r.id}>
                            <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDate(r.entry_date)}</td>
                            <td>
                              {i === 0 && (
                                <span style={{ background: '#e8f0fe', color: '#1a73e8', borderRadius: 8,
                                  padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{r.item_type || '—'}</span>
                              )}
                            </td>
                            <td style={{ fontSize: 13, maxWidth: 200 }}>{r.description}</td>
                            <td style={{ ...tdR, fontSize: 13 }}>₹{fmt(r.amount)}</td>
                            <td style={{ ...tdR, fontSize: 13, color: '#1a73e8' }}>₹{fmt(r._typeCum)}</td>
                            <td style={{ ...tdR, fontSize: 13, color: '#137333' }}>₹{fmt(r._runCum)}</td>
                          </tr>
                        )),
                        // Subtotal row after each type group
                        <tr key={`sub-${block.type}`} style={SUB}>
                          <td colSpan={3} style={{ padding: '7px 12px', fontSize: 13 }}>
                            {block.type} — Subtotal ({block.rows.length} {block.rows.length === 1 ? 'entry' : 'entries'})
                          </td>
                          <td style={{ ...tdR, padding: '7px 12px', color: '#c5221f' }}>
                            ₹{fmt(block.rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0))}
                          </td>
                          <td style={{ ...tdR, padding: '7px 12px', color: '#1a73e8' }}>
                            ₹{fmt(block.rows[block.rows.length - 1]._typeCum)}
                          </td>
                          <td style={{ ...tdR, padding: '7px 12px', color: '#137333' }}>
                            ₹{fmt(block.rows[block.rows.length - 1]._runCum)}
                          </td>
                        </tr>,
                      ])}
                    </tbody>
                    <tfoot>
                      {seedCostVal > 0 && (
                        <tr style={{ background: '#f0fdf4' }}>
                          <td colSpan={3} style={{ fontWeight: 600, padding: '8px 12px' }}>
                            <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 8,
                              padding: '2px 8px', fontSize: 12 }}>SEED</span>
                            <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
                              Seed Cost {cropCode ? `— ${cropCode}` : ''}
                            </span>
                          </td>
                          <td style={{ ...tdR, fontWeight: 600, padding: '8px 12px', color: '#166534' }}>₹{fmt(seedCostVal)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      )}
                      <tr style={GRAND}>
                        <td colSpan={3} style={{ padding: '10px 12px', fontSize: 14 }}>GRAND TOTAL</td>
                        <td style={{ ...tdR, padding: '10px 12px', color: '#c5221f', fontSize: 14 }}>₹{fmt(grandTotal)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── View Expenses tab ── */}
      {tab === 'view' && (() => {
        // Group rows by item_type in the order they first appear
        const grouped = [];
        const typeMap = new Map();
        rows.forEach(r => {
          const t = r.item_type || 'OTHER';
          if (!typeMap.has(t)) { const g = { type: t, rows: [] }; typeMap.set(t, g); grouped.push(g); }
          typeMap.get(t).rows.push(r);
        });

        const grandAmt      = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
        const totalKg       = parseFloat(viewHarvest?.totalKg || 0);
        const totalSaleAmt  = parseFloat(viewHarvest?.totalSaleAmt || 0);
        const avgSalePerKg  = totalKg > 0 ? totalSaleAmt / totalKg : 0;
        const allCostTotal  = grandAmt + seedCostVal;
        const avgProdPerKg  = totalKg > 0 ? allCostTotal / totalKg : 0;

        // Per-type totals
        const typeTotal = {};
        grouped.forEach(g => { typeTotal[g.type] = g.rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0); });

        // Number formatters
        const fN = (v) => { const n = parseFloat(v || 0); return n === 0 ? '-' : n.toLocaleString('en-IN', { maximumFractionDigits: 0 }); };
        const fNou = (v) => { const n = parseFloat(v || 0); return n.toFixed(2); };

        // Shared cell style
        const B = '1px solid #b0b0c0';
        const cs = { padding: '6px 10px', border: B, verticalAlign: 'middle', fontSize: 13 };
        const hs = { ...cs, fontWeight: 700, textAlign: 'center', background: '#b8b870', color: '#7b0000', fontSize: 12, padding: '9px 8px' };
        const nr = { ...cs, textAlign: 'right' };

        // Cost-impact rows (left and right panels)
        const leftItems  = ['LEASE','SEED','FEED','CHEMICAL','CHEMICALS'];
        const rightItems = ['MAINTENANCE','FARM BOYS','FARM BOYs','ELE. BILLS','ELE. BILLs','HARVEST'];
        const perKg = (type) => {
          const t = typeTotal[type] || 0;
          return totalKg > 0 && t > 0 ? Math.round(t / totalKg) : null;
        };
        // Build matched pairs for cost-impact grid from actual types present
        const ciLeft  = grouped.filter(g => leftItems.some(l => g.type.toUpperCase().includes(l.replace(' ','')))).slice(0,5);
        const ciRight = grouped.filter(g => rightItems.some(r => g.type.toUpperCase().includes(r.replace(' ','')))).slice(0,4);

        return (
          <div style={{ overflowX: 'auto' }}>
            {viewLoading && <div className="loading-msg" style={{ marginBottom: 12 }}>Loading harvest data…</div>}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif', minWidth: 700 }}>
              <tbody>

                {/* ── SUMMARY header ── */}
                <tr>
                  <td colSpan={6} style={{ background: '#1a237e', color: '#fff', padding: '13px 18px', fontSize: 17, fontWeight: 800, letterSpacing: 1, border: '2px solid #0d1b6e' }}>
                    SUMMARY : {cropCode || 'All Crops'}
                  </td>
                  <td style={{ background: '#1a237e', color: '#fff', padding: '13px 10px', fontSize: 18, fontWeight: 700, textAlign: 'center', border: '2px solid #0d1b6e' }}>₹</td>
                  <td style={{ background: '#1a237e', color: '#c5221f', padding: '13px 18px', fontSize: 20, fontWeight: 900, textAlign: 'right', textDecoration: 'underline', border: '2px solid #0d1b6e', whiteSpace: 'nowrap' }}>
                    {fN(grandAmt)}
                  </td>
                </tr>

                <tr><td colSpan={8} style={{ height: 10, background: '#fff' }}></td></tr>

                {/* ── XPENSES title row ── */}
                <tr>
                  <td colSpan={6} style={{ background: '#d0cce8', color: '#8b0000', padding: '14px 18px', fontSize: 24, fontWeight: 900, letterSpacing: 3, border: B }}>
                    XPENSES...
                  </td>
                  <td style={{ background: '#8b3a3a', color: '#fff', padding: '14px 10px', fontSize: 18, fontWeight: 800, textAlign: 'center', fontStyle: 'italic', border: B }}>
                    Rs.
                  </td>
                  <td style={{ background: '#8b3a3a', border: B }}></td>
                </tr>

                {/* ── Column headers ── */}
                <tr>
                  <th style={{ ...hs, width: 50 }}>No</th>
                  <th style={{ ...hs, width: 110 }}>ITEM</th>
                  <th style={{ ...hs, textAlign: 'left', minWidth: 220 }}>DESCRIPTION</th>
                  <th style={{ ...hs, width: 52 }}>UOM</th>
                  <th style={{ ...hs, width: 70 }}>NOU</th>
                  <th style={{ ...hs, width: 80 }}>RPU</th>
                  <th style={{ ...hs, width: 90 }}>AMOUNT</th>
                  <th style={{ ...hs, width: 90 }}>TOTAL</th>
                </tr>

                {/* ── Data rows grouped by type ── */}
                {rows.length === 0
                  ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#888', border: B }}>No expense data{!filterCrop ? ' — select a crop first' : ''}</td></tr>
                  : grouped.map((g, gi) => {
                      const typeSum = g.rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
                      const rowBg   = gi % 2 === 0 ? '#f6f5fc' : '#ffffff';
                      return g.rows.map((r, ri) => (
                        <tr key={r.id} style={{ background: rowBg }}>
                          {ri === 0 && (
                            <td rowSpan={g.rows.length} style={{ ...cs, textAlign: 'center', fontWeight: 900, fontSize: 26, color: '#1a237e', background: '#eeeef8', lineHeight: 1 }}>
                              {gi + 1}
                            </td>
                          )}
                          {ri === 0 && (
                            <td rowSpan={g.rows.length} style={{ ...cs, textAlign: 'center', fontWeight: 700, fontSize: 13, color: '#1a237e', background: '#fff' }}>
                              {g.type}
                            </td>
                          )}
                          <td style={{ ...cs, fontSize: 12 }}>
                            {r.entry_date && <span style={{ color: '#888', fontSize: 11, marginRight: 6 }}>{fmtDate(r.entry_date)}</span>}
                            <span style={{ fontWeight: 500 }}>{(r.vendor_name || r.description || '—').toUpperCase()}</span>
                          </td>
                          <td style={{ ...cs, textAlign: 'center', fontSize: 12, color: '#555' }}>{r.uom}</td>
                          <td style={{ ...nr, color: '#333' }}>{fNou(r.nou)}</td>
                          <td style={{ ...nr, color: '#333' }}>{fN(r.rpu)}</td>
                          <td style={{ ...nr, fontWeight: 600, color: '#222' }}>{fN(r.amount)}</td>
                          {ri === 0 && (
                            <td rowSpan={g.rows.length} style={{ ...nr, fontWeight: 800, fontSize: 16, color: '#1a237e', background: '#f0f0ff', borderLeft: '2px solid #1a237e', whiteSpace: 'nowrap' }}>
                              {fN(typeSum)}
                            </td>
                          )}
                        </tr>
                      ));
                    })
                }

                {/* ── Grand total row ── */}
                {rows.length > 0 && (
                  <tr style={{ background: '#1a237e' }}>
                    <td colSpan={6} style={{ ...cs, color: '#fff', fontWeight: 800, fontSize: 13, textAlign: 'right', border: '2px solid #0d1b6e' }}>
                      GRAND TOTAL
                    </td>
                    <td style={{ ...nr, color: '#fff', fontWeight: 800, fontSize: 14, background: '#1a237e', border: '2px solid #0d1b6e' }}>{fN(grandAmt)}</td>
                    <td style={{ ...nr, color: '#ffd54f', fontWeight: 900, fontSize: 16, background: '#1a237e', border: '2px solid #0d1b6e', whiteSpace: 'nowrap' }}>{fN(grandAmt)}</td>
                  </tr>
                )}

                <tr><td colSpan={8} style={{ height: 14, background: '#fff' }}></td></tr>

                {/* ── Average COST IMPACT section ── */}
                <tr>
                  <td colSpan={8} style={{ background: '#d0cce8', color: '#5a3a8a', padding: '10px 18px', fontSize: 14, fontWeight: 700, fontStyle: 'italic', textAlign: 'center', border: B }}>
                    Average COST IMPACT for the Production ONE Kg of SHRIMP...
                    {totalKg > 0 && <span style={{ marginLeft: 16, color: '#1a237e', fontStyle: 'normal' }}>({fN(totalKg)} Kg harvested)</span>}
                  </td>
                </tr>
                {/* Cost impact two-column layout */}
                <tr>
                  {/* Left panel */}
                  <td colSpan={4} style={{ padding: 0, border: B, verticalAlign: 'top' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {grouped.filter(g => !rightItems.some(k => g.type.toUpperCase().includes(k.split(' ').join(''))))
                          .map(g => {
                            const pkg = totalKg > 0 ? Math.round(typeTotal[g.type] / totalKg) : null;
                            return (
                              <tr key={g.type}>
                                <td style={{ padding: '7px 12px', fontWeight: 700, fontSize: 13, color: '#1a237e', width: '65%', borderBottom: '1px solid #e0e0ee' }}>{g.type}</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', color: '#1a237e', fontWeight: 700, borderBottom: '1px solid #e0e0ee', whiteSpace: 'nowrap' }}>₹</td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 800, fontSize: 14, color: '#1a237e', borderBottom: '1px solid #e0e0ee', whiteSpace: 'nowrap' }}>
                                  {pkg != null ? pkg : '-'}
                                </td>
                              </tr>
                            );
                          })}
                        {seedCostVal > 0 && (
                          <tr>
                            <td style={{ padding: '7px 12px', fontWeight: 700, fontSize: 13, color: '#137333', borderBottom: '1px solid #e0e0ee' }}>SEED COST</td>
                            <td style={{ padding: '7px 8px', textAlign: 'center', color: '#137333', fontWeight: 700, borderBottom: '1px solid #e0e0ee' }}>₹</td>
                            <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 800, fontSize: 14, color: '#137333', borderBottom: '1px solid #e0e0ee', whiteSpace: 'nowrap' }}>
                              {totalKg > 0 ? Math.round(seedCostVal / totalKg) : '-'}
                            </td>
                          </tr>
                        )}
                        <tr style={{ background: '#c5221f' }}>
                          <td style={{ padding: '9px 12px', fontWeight: 800, fontSize: 13, color: '#fff' }}>AVG. PROD. Cost / Kg</td>
                          <td style={{ padding: '9px 8px', textAlign: 'center', color: '#fff', fontWeight: 800 }}>₹</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 900, fontSize: 16, color: '#fff', whiteSpace: 'nowrap' }}>
                            {avgProdPerKg > 0 ? Math.round(avgProdPerKg) : '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                  {/* Right panel */}
                  <td colSpan={4} style={{ padding: 0, border: B, verticalAlign: 'top' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {grouped.filter(g => rightItems.some(k => g.type.toUpperCase().includes(k.split(' ').join(''))))
                          .map(g => {
                            const pkg = totalKg > 0 ? Math.round(typeTotal[g.type] / totalKg) : null;
                            return (
                              <tr key={g.type}>
                                <td style={{ padding: '7px 12px', fontWeight: 700, fontSize: 13, color: '#1a237e', width: '65%', borderBottom: '1px solid #e0e0ee' }}>{g.type}</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', color: '#1a237e', fontWeight: 700, borderBottom: '1px solid #e0e0ee', whiteSpace: 'nowrap' }}>₹</td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 800, fontSize: 14, color: '#1a237e', borderBottom: '1px solid #e0e0ee', whiteSpace: 'nowrap' }}>
                                  {pkg != null ? pkg : '-'}
                                </td>
                              </tr>
                            );
                          })}
                        <tr style={{ background: '#2e7d32' }}>
                          <td style={{ padding: '9px 12px', fontWeight: 800, fontSize: 13, color: '#fff' }}>AVG. SALE Price / Kg</td>
                          <td style={{ padding: '9px 8px', textAlign: 'center', color: '#fff', fontWeight: 800 }}>₹</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 900, fontSize: 16, color: '#fff', whiteSpace: 'nowrap' }}>
                            {avgSalePerKg > 0 ? Math.round(avgSalePerKg) : '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

              </tbody>
            </table>
          </div>
        );
      })()}

      {/* ── Expense Types management tab ── */}
      {tab === 'types' && (
        <div className="card">
          <div className="card-header"><div className="card-title">⚙️ Manage Expense Types</div></div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>

              {/* Type list */}
              <div style={{ flex: 1, minWidth: 260 }}>
                <table className="data-table">
                  <thead>
                    <tr><th>#</th><th>Expense Type</th>{canWrite && <th>Actions</th>}</tr>
                  </thead>
                  <tbody>
                    {expenseTypes.length === 0 && (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No types yet</td></tr>
                    )}
                    {expenseTypes.map((t, i) => (
                      <tr key={t.id} style={{ background: typeEditId === t.id ? 'rgba(26,115,232,.07)' : undefined }}>
                        <td style={{ color: '#888', fontSize: 12 }}>{i + 1}</td>
                        <td><strong>{t.name}</strong></td>
                        {canWrite && (
                          <td>
                            <button className="btn-sm btn-secondary" onClick={() => handleTypeEdit(t)} style={{ marginRight: 4 }}>Edit</button>
                            {canDelete && <button className="btn-sm btn-danger" onClick={() => handleTypeDelete(t.id)}>Del</button>}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add / Edit form */}
              {canWrite && (
                <div style={{ width: 280, flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#202124' }}>
                    {typeEditId ? '✏️ Edit Type' : '➕ New Expense Type'}
                  </div>
                  {typeErr && <div className="alert alert-error" style={{ marginBottom: 10, fontSize: 13 }}>{typeErr}</div>}
                  <div className="form-group">
                    <label className="form-label">Type Name</label>
                    <input
                      className="form-control"
                      placeholder="e.g. IRRIGATION"
                      value={typeForm.name}
                      onChange={e => setTypeForm({ name: e.target.value.toUpperCase() })}
                      onKeyDown={e => e.key === 'Enter' && handleTypeSave()}
                    />
                    <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>Saved in UPPERCASE automatically</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-primary" onClick={handleTypeSave} disabled={typeSaving || !typeForm.name.trim()}>
                      {typeSaving ? 'Saving…' : typeEditId ? 'Update' : 'Add Type'}
                    </button>
                    {typeEditId && (
                      <button className="btn btn-secondary" onClick={handleTypeCancel}>Cancel</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
